/**
 * Types for the Auto-Investing Comparison Tool.
 *
 * `RawService` matches the response shape from
 *   GET /functions/v1/public-services?lang=...
 * `Service` is our normalized client-friendly version with parsed numeric
 * fields (used for sliders + sorting), language-aware FI license detection,
 * and canonical UI ids for categories / focus / markets / asset classes.
 *
 * Mirrors the conventions used by the sibling `broker-comparison-tool`
 * package — see `../broker-comparison-tool/types.ts` for the original.
 */

export type ServiceType =
  | 'robo_advisor'
  | 'copy_trading'
  | 'managed_fund'
  | 'signal_service'
  | '';

export interface RawService {
  id: string;
  team_id: string;
  title: string;
  slug: string | null;
  tagline: string | null;
  logo_url: string | null;
  language: string;
  year_created: number | null;
  sort_order: number | null;

  service_type: ServiceType | string | null;

  total_fee_value: string | null;
  total_fee_description: string | null;
  min_deposit_value: string | null;
  min_deposit_description: string | null;
  historical_return_value: string | null;
  historical_return_description: string | null;
  risk_levels_value: string | null;
  risk_levels_description: string | null;
  withdrawal_time_value: string | null;
  withdrawal_time_description: string | null;
  app_rating_value: string | null;
  app_rating_description: string | null;

  key_features: string[] | null;
  key_downsides: string[] | null;

  categories: string[] | null;
  focus: string[] | null;
  markets: string[] | null;
  asset_classes: string[] | null;

  monthly_savings: boolean | null;
  sustainable: boolean | null;
  auto_rebalancing: boolean | null;
  tax_optimization: boolean | null;
  bank_id: boolean | null;
  mobile_app: boolean | null;
  human_advisor: boolean | null;
  copy_trading: boolean | null;
  fractional_shares: boolean | null;
  goal_planning: boolean | null;

  security_text: string | null;
  tax_text: string | null;
  strategy_text: string | null;
  pricing_text: string | null;
  offer_text: string | null;
  review_text: string | null;
  affiliate_cta_url: string | null;
  affiliate_cta_label: string | null;
  review_cta_url: string | null;
  review_cta_label: string | null;

  created_at?: string;
  updated_at?: string;
}

export interface Service {
  id: string;
  title: string;
  slug: string;
  tagline: string;
  logoUrl: string | null;
  yearCreated: number | null;

  /**
   * Canonical service category. Empty string when the upstream value isn't
   * one of the four allowed values (treated as unknown).
   */
  serviceType: ServiceType;

  totalFeeValue: string;
  totalFeeDescription: string;
  minDepositValue: string;
  minDepositDescription: string;
  historicalReturnValue: string;
  historicalReturnDescription: string;
  /**
   * True when the service publishes a clearly-labeled historical figure.
   * Distinct from `historicalReturnPercent`, which is the parsed number.
   * Used by the card to decide whether to render the metric box at all.
   */
  hasHistoricalReturn: boolean;
  riskLevelsValue: string;
  riskLevelsDescription: string;
  withdrawalTimeValue: string;
  withdrawalTimeDescription: string;
  appRatingValue: string;
  appRatingDescription: string;

  // Parsed numeric fields used by sliders / sorting
  totalFeePercent: number;
  minDepositNumber: number;
  historicalReturnPercent: number;
  riskLevelsCount: number;
  appRatingNumber: number;

  keyFeatures: string[];
  keyDownsides: string[];

  /** Canonical ids — see ACCOUNT_TYPE_OPTIONS / ASSET_CLASS_OPTIONS / etc. */
  accountTypes: string[];
  assetClasses: string[];
  marketIds: string[];
  /** Empty string = unknown / not clearly positioned. */
  focusId: string;

  fiLicensed: boolean;
  monthlySavings: boolean;
  sustainable: boolean;
  autoRebalancing: boolean;
  taxOptimization: boolean;
  bankId: boolean;
  mobileApp: boolean;
  humanAdvisor: boolean;
  copyTrading: boolean;
  fractionalShares: boolean;
  goalPlanning: boolean;
  hasOffer: boolean;

  securityText: string;
  taxText: string;
  strategyText: string;
  pricingText: string;
  offerText: string;
  reviewText: string;
  affiliateCtaUrl: string | null;
  affiliateCtaLabel: string;
  reviewCtaUrl: string | null;
  reviewCtaLabel: string;

  /** ISO date strings if available — used for Review structured data. */
  createdAt: string | null;
  updatedAt: string | null;
}

export type SortKey =
  | 'default'
  | 'lowestFee'
  | 'lowestDeposit'
  | 'highestReturn'
  | 'highestRating';

export const SORT_KEYS: SortKey[] = [
  'default',
  'lowestFee',
  'lowestDeposit',
  'highestReturn',
  'highestRating',
];

export type FilterState = {
  sortBy: SortKey;
  maxFee: number;
  maxDeposit: number;
  /** Multi-select on canonical service_type ids. */
  serviceTypes: ServiceType[];
  accounts: string[];
  assetClasses: string[];
  markets: string[];
  /** Empty string = all focus areas. Single-select (not array). */
  focus: string;
  fiOnly: boolean;
  hasOfferOnly: boolean;
  sustainableOnly: boolean;
  monthlySavingsOnly: boolean;
  autoRebalancingOnly: boolean;
  taxOptimizationOnly: boolean;
  bankIdOnly: boolean;
  mobileAppOnly: boolean;
  humanAdvisorOnly: boolean;
  goalPlanningOnly: boolean;
};

export interface SliderBounds {
  min: number;
  max: number;
  step: number;
  /** True when min === max — slider would be useless and should be hidden. */
  degenerate: boolean;
}

// Service-type options (global — same enum across all languages).
// Labels are pulled from translations; only the id is stable.
export const SERVICE_TYPE_OPTIONS = [
  { id: 'robo_advisor' },
  { id: 'copy_trading' },
  { id: 'managed_fund' },
  { id: 'signal_service' },
] as const satisfies ReadonlyArray<{ id: Exclude<ServiceType, ''> }>;

// Account-type options. Match arrays cover all three languages so the same
// canonical id resolves regardless of the language the data came from.
export const ACCOUNT_TYPE_OPTIONS = [
  { id: 'ISK', match: ['ISK'] },
  { id: 'KF', match: ['Kapitalförsäkring', 'KF'] },
  { id: 'ASK', match: ['ASK'] },
  { id: 'IPS', match: ['IPS'] },
  { id: 'Pension', match: ['Tjänstepension', 'Pensjonskonto', 'SIPP', 'Pension'] },
  { id: 'Custody', match: ['Depå', 'GIA'] },
  { id: 'ISA', match: ['ISA'] },
  { id: 'JuniorISA', match: ['Junior ISA'] },
  { id: 'Corporate', match: ['Företagskonto', 'Bedriftskonto', 'Corporate'] },
] as const;

// Asset-class options. Canonical ids are language-agnostic.
export const ASSET_CLASS_OPTIONS = [
  { id: 'stocks', match: ['Aktier', 'Aksjer', 'Stocks'] },
  {
    id: 'bonds',
    match: ['Räntor/Obligationer', 'Renter/Obligasjoner', 'Bonds/Fixed income'],
  },
  { id: 'funds', match: ['Fonder', 'Fond', 'Mutual funds'] },
  { id: 'etf', match: ['ETF:er', 'ETF-er', 'ETFs'] },
  { id: 'crypto', match: ['Krypto', 'Crypto'] },
  { id: 'commodities', match: ['Råvaror', 'Råvarer', 'Commodities'] },
  {
    id: 'alternatives',
    match: [
      'Alternativa investeringar',
      'Alternative investeringer',
      'Alternative investments',
    ],
  },
] as const;

// Market exposure options. Note: 'cryptoMarket' is distinct from the
// asset-class 'crypto' so the same string ("Krypto" / "Crypto") can route
// to both axes when present in the upstream data.
export const MARKET_OPTIONS = [
  { id: 'sweden', match: ['Sverige', 'Sweden'] },
  { id: 'norway', match: ['Norge'] },
  { id: 'nordics', match: ['Norden', 'Nordics'] },
  { id: 'usa', match: ['USA'] },
  { id: 'europe', match: ['Europa', 'Europe'] },
  {
    id: 'emerging',
    match: ['Tillväxtmarknader', 'Vekstmarkeder', 'Emerging markets'],
  },
  { id: 'global', match: ['Globalt', 'Global'] },
  { id: 'cryptoMarket', match: ['Krypto', 'Crypto'] },
] as const;

// Focus / strategy options.
export const FOCUS_OPTIONS = [
  { id: 'passive', match: ['Passiv indexering', 'Passiv indeksering', 'Passive indexing'] },
  { id: 'active', match: ['Aktiv förvaltning', 'Aktiv forvaltning', 'Active management'] },
  { id: 'esg', match: ['Hållbart / ESG', 'Bærekraftig / ESG', 'Sustainable / ESG'] },
  { id: 'copyTrading', match: ['Copy trading'] },
  { id: 'thematic', match: ['Tematisk', 'Thematic'] },
] as const;
